import React, { useState, useEffect } from 'react';
import './EditClient.css';
import { API_URL } from "./API";

const EditClient = ({ onBack, onClientUpdated, clientData }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    gst_no: '',
    name: '',
    contact_number: '',
    email: '',
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    pan_no: '',
    state: '',
    address: '',
    status: 'Active',
    client_logo: null
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);

  const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    if (clientData) {
      const newFormData = {
        client_name: clientData.client_name || '',
        gst_no: clientData.gst_no || clientData.gstin || '',
        name: clientData.name || '',
        contact_number: clientData.contact_number || '',
        email: clientData.email || '',
        bank_name: clientData.bank_name || '',
        bank_account_number: clientData.bank_account_number || '',
        ifsc_code: clientData.ifsc_code || '',
        pan_no: clientData.pan_no || '',
        state: clientData.state || '',
        address: clientData.address || '',
        status: clientData.status || 'Active',
        client_logo: clientData.client_logo || null
      };
      
      setFormData(newFormData);
      
      if (clientData.client_logo) {
        const logoUrl = clientData.client_logo.startsWith('http') 
          ? clientData.client_logo 
          : `${API_URL}/${clientData.client_logo}`;
        setLogoPreview(logoUrl);
      }
      setLogoChanged(false);
    }
  }, [clientData]);



const handleInputChange = (e) => {
  const { name, value } = e.target;
  
  if (name === 'contact_number') {
    
    const digitsOnly = value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: digitsOnly
    }));
  } else if (name === 'pan_no') {
    
    const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setFormData(prev => ({
      ...prev,
      [name]: alphanumericOnly
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }
  
  if (errors[name]) {
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }
};
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, WEBP)';
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return 'File size should be less than 5MB';
    }
    
    return null;
  };

const validatePhoneNumber = (value) => {
  if (value.length === 0) {
    return null;
  }
  if (value.length !== 10 || !/^\d{10}$/.test(value)) {
    return 'Phone number must be exactly 10 digits';
  }
  return null;
};

 const validatePAN = (value) => {
    if (value === '') return '';
    if (value.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return '';
  };

  const processFile = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors(prev => ({
        ...prev,
        client_logo: error
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      
      setFormData(prev => ({
        ...prev,
        client_logo: base64String
      }));
      
      setLogoPreview(base64String);
      setLogoChanged(true);
    };
    reader.readAsDataURL(file);

    if (errors.client_logo) {
      setErrors(prev => ({
        ...prev,
        client_logo: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      client_logo: null
    }));
    setLogoPreview(null);
    setLogoChanged(true);
    
    const fileInput = document.getElementById('editClientLogo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const updateClient = async (clientData) => {
    try {
      const response = await fetch(`${API_URL}/clients/update_client`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(clientData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setErrors({});

    const phoneError = validatePhoneNumber(formData.contact_number);
    const panError = validatePAN(formData.pan_no);

    if (phoneError || panError) {
      setErrors({
        contact_number: phoneError,
        pan_no: panError
      });
      setIsLoading(false);
      return;
    }

    try {
      const updateData = {
        client_id: clientData.client_id || clientData.id,
        client_name: formData.client_name.trim(),
        gst_no: formData.gst_no.trim(),
        name: formData.name.trim(),
        contact_number: formData.contact_number.trim(),
        email: formData.email.trim(),
        bank_name: formData.bank_name.trim(),
        bank_account_number: formData.bank_account_number.trim(),
        ifsc_code: formData.ifsc_code.trim(),
        pan_no: formData.pan_no.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        status: formData.status
      };

      if (logoChanged) {
        updateData.client_logo = formData.client_logo;
      }

      const updateResult = await updateClient(updateData);
      
      const updatedClient = {
        ...clientData,
        client_name: updateResult.data.client_name,
        gst_no: updateResult.data.gst_no,
        name: updateResult.data.name,
        contact_number: updateResult.data.contact_number,
        email: updateResult.data.email,
        bank_name: updateResult.data.bank_name,
        bank_account_number: updateResult.data.bank_account_number,
        ifsc_code: updateResult.data.ifsc_code,
        pan_no: updateResult.data.pan_no,
        state: updateResult.data.state,
        address: updateResult.data.address,
        status: updateResult.data.status,
        client_logo: updateResult.data.client_logo,
        updated_on: updateResult.data.updated_on,
        originalData: updateResult
      };

      if (onClientUpdated) {
        onClientUpdated(updatedClient);
      }
      
      alert('Client updated successfully!');
      
      if (onBack) {
        onBack();
      }

    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to update client. Please try again.'
      });
      
      alert('Error: ' + (error.message || 'Failed to update client. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="EC-00">
      <div className="EC-01">
        <div className="EC-02">
          <h3 className="EC-03">Edit Client</h3>
        </div>
        
        <div className="EC-04">
          <div className="EC-05">
            {errors.submit && (
              <div className="EC-submit-error">
                {errors.submit}
              </div>
            )}

            <div className="EC-row">
              <div className="EC-12">
                <label className="EC-13" htmlFor="client_name">
                  Client Name
                </label>
                <input
                  type="text"
                  id="client_name"
                  name="client_name"
                  className="EC-15"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Enter Client Name"
                  disabled={isLoading}
                />
              </div>

              <div className="EC-18">
                <label className="EC-19" htmlFor="gst_no">
                  GST Number
                </label>
                <input
                  type="text"
                  id="gst_no"
                  name="gst_no"
                  className="EC-21"
                  value={formData.gst_no}
                  onChange={handleInputChange}
                  placeholder="Enter GST Number"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EC-row">
              <div className="EC-24">
                <label className="EC-25" htmlFor="name">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="EC-27"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Name"
                  disabled={isLoading}
                />
              </div>

              <div className="EC-59">
                <label className="EC-60" htmlFor="contact_number">
                  Contact Number
                </label>
                <input
                  type="text"
                  id="contact_number"
                  name="contact_number"
                  className="EC-61"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter Contact Number"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.contact_number && <span className="EC-38">{errors.contact_number}</span>}
              </div>
            </div>

            <div className="EC-row">
              <div className="EC-62">
                <label className="EC-63" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="EC-64"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  disabled={isLoading}
                />
              </div>

              <div className="EC-59">
                <label className="EC-60" htmlFor="pan_no">
                  PAN Number
                </label>
                <input
                  type="text"
                  id="pan_no"
                  name="pan_no"
                  className="EC-61"
                  value={formData.pan_no}
                  onChange={handleInputChange}
                  placeholder="Enter PAN Number"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.pan_no && <span className="EC-38">{errors.pan_no}</span>}
              </div>
            </div>

            <div className="EC-row">
              <div className="EC-62">
                <label className="EC-63" htmlFor="bank_name">
                  Bank Name
                </label>
                <input
                  type="text"
                  id="bank_name"
                  name="bank_name"
                  className="EC-64"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  disabled={isLoading}
                />
              </div>

              <div className="EC-65">
                <label className="EC-66" htmlFor="bank_account_number">
                  Bank Account Number
                </label>
                <input
                  type="text"
                  id="bank_account_number"
                  name="bank_account_number"
                  className="EC-67"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Account Number"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EC-row">
              <div className="EC-68">
                <label className="EC-69" htmlFor="ifsc_code">
                  IFSC Code
                </label>
                <input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  className="EC-70"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC Code"
                  disabled={isLoading}
                />
              </div>

              <div className="EC-71">
                <label className="EC-72" htmlFor="state">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  className="EC-73"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter State"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EC-74">
              <label className="EC-75" htmlFor="address">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                className="EC-76"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                disabled={isLoading}
                rows="3"
              />
            </div>

            <div className="EC-77">
              <label className="EC-78" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="EC-79"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="EC-30">
              <label className="EC-31" htmlFor="editClientLogo">
                Client Logo
              </label>
              
              <div 
                className={`EC-42 ${isDragOver ? 'EC-43' : ''} ${errors.client_logo ? 'EC-44' : ''} ${isLoading ? 'EC-disabled' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !isLoading && document.getElementById('editClientLogo').click()}
              >
                <input
                  type="file"
                  id="editClientLogo"
                  name="editClientLogo"
                  className="EC-58"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
                
                {!logoPreview ? (
                  <div className="EC-45">
                    <div className="EC-46">
                      <svg className="EC-47" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                    </div>
                    <div className="EC-48">
                      <span className="EC-49">Click to upload</span> or drag and drop
                    </div>
                    <div className="EC-50">
                      PNG, JPG, GIF up to 5MB
                    </div>
                  </div>
                ) : (
                  <div className="EC-51">
                    <img src={logoPreview} alt="Logo preview" className="EC-52" />
                    <div className="EC-53">
                      <button 
                        type="button" 
                        className="EC-54" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }} 
                        title="Remove image"
                        disabled={isLoading}
                      >
                        <svg className="EC-55" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {errors.client_logo && <span className="EC-38">{errors.client_logo}</span>}
            </div>
          </div>

          <div className="EC-39">
            <button 
              type="button" 
              className="EC-40" 
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
            <button 
              type="button" 
              className="EC-41" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditClient;